import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ExpandMore,
  PlayArrow,
  CheckCircle,
  Refresh,
  Assignment,
  Computer,
  Stop
} from '@mui/icons-material';
import axios from 'axios';

const WorkflowController = ({ workflow, workflowKey, workflowState, onStateChange }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [runningAll, setRunningAll] = useState(false);
  const [currentRunningStep, setCurrentRunningStep] = useState(null);
  const [error, setError] = useState(null);
  const [apiKey, setApiKey] = useState(localStorage.getItem('anthropic_api_key') || '');

  // Version tracking for debugging
  const FRONTEND_VERSION = "v2.1.0-" + new Date().toISOString().slice(0, 16).replace('T', ' ');
  
  // Log version for debugging
  React.useEffect(() => {
    console.log(`🚀 Blog Writer Frontend Version: ${FRONTEND_VERSION}`);
    console.log(`📱 Deployed at: ${window.location.href}`);
  }, []);

  // Handle workflow state updates with localStorage persistence
  const updateWorkflowState = (updates) => {
    if (workflowState && onStateChange) {
      const newState = {
        ...workflowState,
        ...updates
      };
      
      // Save to localStorage for persistence between agent calls
      if (updates.results) {
        localStorage.setItem('blog_workflow_results', JSON.stringify(updates.results));
      }
      if (updates.inputText) {
        localStorage.setItem('blog_workflow_topic', updates.inputText);
      }
      
      onStateChange(newState);
    }
  };

  // Send task to A2A agent
  const sendTaskToAgent = async (agentUrl, message, taskId = null) => {
    // Check if API key is provided
    if (!apiKey.trim()) {
      throw new Error('Please provide your Anthropic API key before running the workflow');
    }

    if (!taskId) {
      taskId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    const payload = {
      id: taskId,
      message: {
        role: "user",
        parts: [{ type: "text", text: message }]
      },
      metadata: {
        timestamp: new Date().toISOString(),
        source: "react_dashboard",
        apiKey: apiKey.trim()
      }
    };

    try {
      const response = await axios.post(`${agentUrl}/tasks/send`, payload, {
        headers: {
          'Content-Type': 'application/json'
          // Remove X-Anthropic-API-Key header to avoid conflicts
        },
        timeout: 300000 // 5 minute timeout
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Invalid API key. Please check your Anthropic API key and try again.');
      }
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Save API key to localStorage when it changes
  const handleApiKeyChange = (newApiKey) => {
    setApiKey(newApiKey);
    if (newApiKey.trim()) {
      localStorage.setItem('anthropic_api_key', newApiKey.trim());
    } else {
      localStorage.removeItem('anthropic_api_key');
    }
  };

  // Run all steps sequentially
  const handleRunAll = async () => {
    if (!workflow?.steps || !workflow?.agents || !workflowState || !workflowState.inputText.trim()) {
      setError("Please enter a blog topic before running the workflow");
      return;
    }

    if (!apiKey.trim()) {
      setError("Please provide your Anthropic API key before running the workflow");
      return;
    }

    setRunningAll(true);
    setError(null);

    try {
      for (let stepIndex = 0; stepIndex < workflow.steps.length; stepIndex++) {
        setCurrentRunningStep(stepIndex);
        setActiveStep(stepIndex);
        
        // Check if step is already completed
        const step = workflow.steps[stepIndex];
        if (workflowState.stepCompleted?.[step.key]) {
          continue; // Skip already completed steps
        }

        await executeStep(stepIndex);
        
        // Small delay between steps for better UX
        if (stepIndex < workflow.steps.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      setCurrentRunningStep(null);
      setActiveStep(workflow.steps.length - 1); // Set to final step
      
    } catch (err) {
      setError(`Workflow failed at step ${currentRunningStep + 1}: ${err.message}`);
    } finally {
      setRunningAll(false);
      setCurrentRunningStep(null);
    }
  };

  // Stop running all steps
  const handleStopAll = () => {
    setRunningAll(false);
    setCurrentRunningStep(null);
    setLoading(false);
  };

  // Execute a single step with localStorage integration
  const executeStep = async (stepIndex) => {
    const step = workflow.steps[stepIndex];
    const agent = workflow.agents[step.agent];
    
    if (!agent) {
      throw new Error(`Agent ${step.agent} not found`);
    }

    // Load previous results from localStorage for context
    const savedResults = JSON.parse(localStorage.getItem('blog_workflow_results') || '{}');
    const currentResults = { ...workflowState.results, ...savedResults };

    let stepInput = "";

    if (stepIndex === 0) {
      // First step uses user input
      stepInput = workflowState.inputText;
    } else if (stepIndex === workflow.steps.length - 1) {
      // Final step gets combined input from all previous steps
      stepInput = `# Blog Topic
${workflowState.inputText}

## Original Draft
${currentResults.initial?.content || ''}

## Beginner Critique
${currentResults.beginner?.content || ''}

## Expert Critique  
${currentResults.expert?.content || ''}`;
    } else {
      // Middle steps (critics) need to see the original draft explicitly
      const initialDraft = currentResults.initial?.content || '';
      if (!initialDraft) {
        throw new Error('Initial draft not found. Please run the Initial Writer first.');
      }
      stepInput = `ORIGINAL DRAFT:
${initialDraft}

Please provide your critique of the above blog draft.`;
    }

    // Send task to agent
    const result = await sendTaskToAgent(agent.url, stepInput);

    if (result.error) {
      throw new Error(result.error);
    }

    // Extract content from result
    const taskData = result.task || {};
    const artifacts = taskData.artifacts || [];

    if (artifacts.length > 0) {
      const content = artifacts[0].parts?.[0]?.text || "No content";
      
      // Update workflow state with real-time display
      const newResults = {
        ...currentResults,
        [step.key]: {
          content: content,
          task_data: taskData,
          timestamp: new Date().toISOString()
        }
      };

      const newStepCompleted = {
        ...workflowState.stepCompleted,
        [step.key]: true
      };

      // Store in localStorage immediately for persistence
      localStorage.setItem('blog_workflow_results', JSON.stringify(newResults));
      localStorage.setItem(`blog_${step.key}_content`, content);

      updateWorkflowState({
        results: newResults,
        stepCompleted: newStepCompleted
      });
    } else {
      throw new Error("No results returned from agent");
    }
  };

  // Handle individual step execution
  const handleStepRun = async (stepIndex) => {
    if (!workflow?.steps || !workflow?.agents || !workflowState) {
      return;
    }

    if (!apiKey.trim()) {
      setError("Please provide your Anthropic API key before running any steps");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await executeStep(stepIndex);
      
      // Move to next step if not final
      if (stepIndex < workflow.steps.length - 1) {
        setActiveStep(stepIndex + 1);
      }

    } catch (err) {
      const stepName = workflow.steps[stepIndex]?.name || `Step ${stepIndex + 1}`;
      setError(`Failed to run ${stepName}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Reset workflow
  const handleReset = () => {
    updateWorkflowState({
      inputText: "",
      results: {},
      stepCompleted: {
        initial: false,
        beginner: false,
        expert: false,
        final: false
      }
    });
    setActiveStep(0);
    setError(null);
  };

  // Render planned workflow
  if (workflow.status === 'planned') {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ mr: 2 }}>
            {workflow.icon}
          </Typography>
          <Box>
            <Typography variant="h5">
              {workflow.name}
            </Typography>
            <Chip label="Planned" color="warning" size="small" sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {workflow.description}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          📋 <strong>Status:</strong> This workflow is planned for future implementation. 
          It will follow the same A2A pattern as Blog Writer 2.
        </Alert>

        {workflowKey === 'lead_magnet_writer' && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Planned Features:</Typography>
            <List>
              <ListItem>
                <ListItemIcon><Assignment /></ListItemIcon>
                <ListItemText 
                  primary="Gap Analysis Agent" 
                  secondary="Extract knowledge gaps from blog feedback" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Assignment /></ListItemIcon>
                <ListItemText 
                  primary="Strategy Agent" 
                  secondary="Determine optimal lead magnet type and approach" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Assignment /></ListItemIcon>
                <ListItemText 
                  primary="Content Creator Agent" 
                  secondary="Generate complete lead magnet content" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Assignment /></ListItemIcon>
                <ListItemText 
                  primary="Conversion Optimizer Agent" 
                  secondary="Create landing pages and email sequences" 
                />
              </ListItem>
            </List>
          </Box>
        )}
      </Paper>
    );
  }

  if (!workflowState) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Loading workflow...</Typography>
      </Paper>
    );
  }

  const allStepsCompleted = workflowState?.stepCompleted?.final || false;

  return (
    <Box>
      {/* Workflow Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ mr: 2 }}>
            {workflow.icon}
          </Typography>
          <Box>
            <Typography variant="h5">
              {workflow.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {workflow.description}
            </Typography>
          </Box>
        </Box>

        {/* Input Section */}
        <TextField
          fullWidth
          label="Blog Topic Description"
          placeholder="Describe the topic you want to write about..."
          multiline
          rows={3}
          value={workflowState.inputText || ''}
          onChange={(e) => updateWorkflowState({ inputText: e.target.value })}
          sx={{ mb: 2 }}
        />

        {/* API Key Input */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            🔑 Anthropic API Key
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Enter your Anthropic API key"
            value={apiKey}
            onChange={(e) => handleApiKeyChange(e.target.value)}
            placeholder="sk-ant-api03-..."
            helperText="Your API key is stored locally and sent securely to the agents. Get one from console.anthropic.com"
            sx={{ mb: 1 }}
          />
          {!apiKey.trim() && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <strong>API Key Required:</strong> You need an Anthropic API key to use the blog writing agents. 
              <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '5px' }}>
                Get one here →
              </a>
            </Alert>
          )}
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {/* Run All Button */}
          <Button
            variant="contained"
            size="large"
            startIcon={runningAll ? <Stop /> : <PlayArrow />}
            onClick={runningAll ? handleStopAll : handleRunAll}
            disabled={!workflowState.inputText?.trim() || !apiKey.trim() || loading}
            color={runningAll ? "error" : "primary"}
            sx={{ minWidth: 160 }}
          >
            {runningAll ? "Stop Workflow" : "🚀 Run All Steps"}
          </Button>

          {/* Reset Button */}
          {Object.keys(workflowState.results || {}).length > 0 && (
            <Button
              startIcon={<Refresh />}
              onClick={handleReset}
              disabled={runningAll || loading}
            >
              Start New Blog Workflow
            </Button>
          )}
        </Box>

        {/* Running All Progress */}
        {runningAll && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 2 }} />
              <Typography variant="body2">
                {currentRunningStep !== null && workflow.steps?.[currentRunningStep] ? 
                  `Running ${workflow.steps[currentRunningStep].icon} ${workflow.steps[currentRunningStep].name}...` :
                  "Preparing workflow..."
                }
              </Typography>
            </Box>
          </Alert>
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Workflow Steps */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Workflow Progress
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          {workflow.steps?.map((step, index) => {
            const isCompleted = workflowState.stepCompleted?.[step.key] || false;
            const result = workflowState.results?.[step.key];
            const agent = workflow.agents?.[step.agent];

            return (
              <Step key={step.key}>
                <StepLabel 
                  icon={isCompleted ? <CheckCircle color="success" /> : (index + 1)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 1 }}>{step.icon}</Typography>
                    <Typography variant="h6">{step.name}</Typography>
                    {agent && (
                      <Chip
                        icon={<Computer />}
                        label={agent.name}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Box>
                </StepLabel>
                
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    {agent && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {agent.description}
                      </Typography>
                    )}

                    {/* Step Controls */}
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant={runningAll && currentRunningStep === index ? "contained" : "outlined"}
                        onClick={() => handleStepRun(index)}
                        disabled={
                          runningAll ||
                          loading || 
                          !workflowState.inputText || 
                          (index > 0 && !workflowState.stepCompleted?.initial)
                        }
                        startIcon={
                          (runningAll && currentRunningStep === index) || loading ? 
                            <CircularProgress size={16} /> : 
                            <PlayArrow />
                        }
                        color={runningAll && currentRunningStep === index ? "primary" : "inherit"}
                      >
                        {(runningAll && currentRunningStep === index) ? 'Running...' : 
                         loading ? 'Running...' : 
                         `Run ${step.name}`}
                      </Button>
                    </Box>

                    {/* Step Result */}
                    {result && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1">
                            {step.icon} {step.name} Result
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <Typography 
                              variant="body2" 
                              component="pre" 
                              sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem'
                              }}
                            >
                              {result.content}
                            </Typography>
                          </Box>
                          {result.timestamp && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Completed: {new Date(result.timestamp).toLocaleString()}
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                </StepContent>
              </Step>
            );
          })}
        </Stepper>

        {/* Completion Celebration */}
        {allStepsCompleted && !runningAll && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              🎉 Blog Writer 2 Workflow Complete! 🎉
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your blog post has been successfully created through all 4 stages of refinement.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Refresh />}
              onClick={handleReset}
              size="large"
            >
              🔄 Start New Blog Workflow
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default WorkflowController;